/**
 * Patients tab on the connection detail view: the connection's patient links
 * with a status filter, a name or external id search, and per-row pause,
 * resume and unlink. Enrolling opens EnrollPatientDialog. The links are loaded
 * by MonitoringDetail when this tab is first opened, and it shows their count
 * on the tab from then on.
 */
import type { ApolloError } from '@apollo/client';
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Icon from '@components/Icon/Icon';

import {
  useSetEhrPatientLinkPaused,
  useUnenrollEhrSyncPatient,
} from '@hooks/api/ehr-sync-patients';
import { useSnackbar } from '@hooks/useSnackbar';

import type { EhrPatientLink, EhrPatientLinkStatus } from '@models/ehr-sync';

import { formatDate } from '@utils/format';

import EnrollPatientDialog from '../../components/EnrollPatientDialog';
import PatientLinkStatusChip from '../../components/PatientLinkStatusChip';
import UnlinkPatientDialog from '../../components/UnlinkPatientDialog';
import {
  LINK_STATUS_ORDER,
  PAUSABLE_LINK_STATUSES,
  patientName,
  sexAbbreviation,
} from '../../components/patient-link.util';
import { linkStatusMessages } from '../../messages';
import StatePanel from '../shared/StatePanel';

const messages = defineMessages({
  title: {
    id: 'ehrSync.detail.patients.title',
    defaultMessage: 'Linked patients',
  },
  enroll: {
    id: 'ehrSync.detail.patients.enroll',
    defaultMessage: 'Enroll patient',
  },
  all: { id: 'ehrSync.detail.patients.filter.all', defaultMessage: 'All' },
  filterLabel: {
    id: 'ehrSync.detail.patients.filter.label',
    defaultMessage: 'Filter by link status',
  },
  search: {
    id: 'ehrSync.detail.patients.search',
    defaultMessage: 'Search by name or external ID',
  },
  patient: {
    id: 'ehrSync.detail.patients.column.patient',
    defaultMessage: 'Patient',
  },
  externalId: {
    id: 'ehrSync.detail.patients.column.externalId',
    defaultMessage: 'External patient ID',
  },
  status: {
    id: 'ehrSync.detail.patients.column.status',
    defaultMessage: 'Link status',
  },
  match: {
    id: 'ehrSync.detail.patients.column.match',
    defaultMessage: 'Match',
  },
  enrolled: {
    id: 'ehrSync.detail.patients.column.enrolled',
    defaultMessage: 'Enrolled',
  },
  lastSynced: {
    id: 'ehrSync.detail.patients.column.lastSynced',
    defaultMessage: 'Last synced',
  },
  actions: {
    id: 'ehrSync.detail.patients.column.actions',
    defaultMessage: 'Actions',
  },
  dob: { id: 'ehrSync.detail.patients.dob', defaultMessage: 'DOB {date}' },
  missingPatient: {
    id: 'ehrSync.detail.patients.missingPatient',
    defaultMessage: 'Patient no longer in Engage',
  },
  waiting: {
    id: 'ehrSync.detail.patients.waiting',
    defaultMessage: 'Waiting for next QIE poll',
  },
  inQueue: {
    id: 'ehrSync.detail.patients.inQueue',
    defaultMessage: 'In the review queue',
  },
  openQueue: {
    id: 'ehrSync.detail.patients.openQueue',
    defaultMessage: 'Review',
  },
  noCandidate: {
    id: 'ehrSync.detail.patients.noCandidate',
    defaultMessage: 'No EHR candidate above the threshold',
  },
  reviewedBy: {
    id: 'ehrSync.detail.patients.reviewedBy',
    defaultMessage: 'Reviewed by {name}',
  },
  rejectedBy: {
    id: 'ehrSync.detail.patients.rejectedBy',
    defaultMessage: 'Rejected by {name}',
  },
  unlinkedOn: {
    id: 'ehrSync.detail.patients.unlinkedOn',
    defaultMessage: 'Unlinked {date}',
  },
  rowMenu: {
    id: 'ehrSync.detail.patients.rowMenu',
    defaultMessage: 'Actions for {name}',
  },
  pause: {
    id: 'ehrSync.detail.patients.pause',
    defaultMessage: 'Pause sync',
  },
  resume: {
    id: 'ehrSync.detail.patients.resume',
    defaultMessage: 'Resume sync',
  },
  unlink: {
    id: 'ehrSync.detail.patients.unlink',
    defaultMessage: 'Unlink patient',
  },
  paused: {
    id: 'ehrSync.detail.patients.paused',
    defaultMessage: 'Sync paused for {name}',
  },
  resumed: {
    id: 'ehrSync.detail.patients.resumed',
    defaultMessage: 'Sync resumed for {name}',
  },
  unlinked: {
    id: 'ehrSync.detail.patients.unlinked',
    defaultMessage: '{name} unlinked',
  },
  enrolledSnack: {
    id: 'ehrSync.detail.patients.enrolledSnack',
    defaultMessage: '{name} enrolled. Matching runs on the next QIE poll.',
  },
  empty: {
    id: 'ehrSync.detail.patients.empty',
    defaultMessage: 'No patients match',
  },
  loadFailed: {
    id: 'ehrSync.detail.patients.loadFailed',
    defaultMessage: "Couldn't load the patients for this connection",
  },
  loadingLinks: {
    id: 'ehrSync.detail.patients.loadingLinks',
    defaultMessage: 'Loading patients…',
  },
  noLinks: {
    id: 'ehrSync.detail.patients.noLinks',
    defaultMessage: 'No patients are enrolled on this connection yet.',
  },
});

const ROWS_PER_PAGE = 10;

type StatusFilter = EhrPatientLinkStatus | 'all';

/** Props for {@link PatientsTab}. */
interface PatientsTabProps {
  /** The connection's registry row. */
  ehrConnectionId: string;
  organizationName: string;
  /** Second line of the enrol dialog header, e.g. vendor and environment. */
  connectionLabel?: string | null;
  /** EHR vendor for the unlink wording, e.g. 'Epic'. */
  vendor?: string | null;
  links: EhrPatientLink[];
  /** True while the links load or reload. */
  loading?: boolean;
  /** Set when the links failed to load; the table is replaced by an error. */
  error?: ApolloError;
  onRetry?: () => void;
  /** Opens the review queue. Omitted while that tab is not available. */
  onOpenReviewQueue?: (linkId: string) => void;
}

/**
 * Patients tab.
 *
 * @param props - see {@link PatientsTabProps}
 */
export default function PatientsTab({
  ehrConnectionId,
  organizationName,
  connectionLabel,
  vendor,
  links,
  loading = false,
  error,
  onRetry,
  onOpenReviewQueue,
}: PatientsTabProps) {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [setPaused] = useSetEhrPatientLinkPaused();
  const [unenroll, unlinking] = useUnenrollEhrSyncPatient();
  // Links with a pause, resume or unlink in flight. Tracked per row because
  // the mutation hooks' loading flags are shared by every row.
  const [busyIds, setBusyIds] = useState<ReadonlySet<string>>(new Set());
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [menu, setMenu] = useState<{
    anchor: HTMLElement;
    linkId: string;
  } | null>(null);
  // Kept after the dialog closes so its text does not change mid-fade.
  const [unlinkTarget, setUnlinkTarget] = useState<EhrPatientLink | null>(null);
  const [unlinkOpen, setUnlinkOpen] = useState(false);
  const [enrollOpen, setEnrollOpen] = useState(false);

  const counts = useMemo(() => {
    const byStatus = new Map<EhrPatientLinkStatus, number>();
    for (const link of links) {
      byStatus.set(link.status, (byStatus.get(link.status) ?? 0) + 1);
    }
    return byStatus;
  }, [links]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return links.filter((link) => {
      if (filter !== 'all' && link.status !== filter) {
        return false;
      }
      if (!needle) {
        return true;
      }
      return [patientName(link.patient), link.externalPatientId].some((value) =>
        value?.toLowerCase().includes(needle)
      );
    });
  }, [links, filter, search]);

  // A refetch can shrink the list under the current page, so clamp rather
  // than trust the stored page.
  const lastPage = Math.max(0, Math.ceil(visible.length / ROWS_PER_PAGE) - 1);
  const currentPage = Math.min(page, lastPage);
  const pageRows = visible.slice(
    currentPage * ROWS_PER_PAGE,
    (currentPage + 1) * ROWS_PER_PAGE
  );
  // Read from the live list so a refetch while the menu is open is reflected.
  const menuLink = menu
    ? links.find((candidate) => candidate.id === menu.linkId)
    : undefined;

  const nameOf = (link: EhrPatientLink) =>
    patientName(link.patient) ?? formatMessage(messages.missingPatient);

  const whileBusy = async (linkId: string, write: () => Promise<boolean>) => {
    setBusyIds((current) => new Set(current).add(linkId));
    try {
      return await write();
    } finally {
      setBusyIds((current) => {
        const next = new Set(current);
        next.delete(linkId);
        return next;
      });
    }
  };

  const togglePause = async (link: EhrPatientLink) => {
    setMenu(null);
    const pausing = link.status !== 'paused';
    const saved = await whileBusy(link.id, () => setPaused(link.id, pausing));
    if (saved) {
      enqueueSnackbar({
        variant: 'success',
        message: formatMessage(pausing ? messages.paused : messages.resumed, {
          name: nameOf(link),
        }),
      });
    }
  };

  const confirmUnlink = async () => {
    if (!unlinkTarget) {
      return;
    }
    const saved = await whileBusy(unlinkTarget.id, () =>
      unenroll(unlinkTarget.id)
    );
    if (saved) {
      enqueueSnackbar({
        variant: 'success',
        message: formatMessage(messages.unlinked, {
          name: nameOf(unlinkTarget),
        }),
      });
      setUnlinkOpen(false);
    }
  };

  const statusNote = (link: EhrPatientLink): React.ReactNode => {
    switch (link.status) {
      case 'pending':
        return formatMessage(messages.waiting);
      case 'needsReview':
        return onOpenReviewQueue ? (
          <Button
            size="small"
            variant="text"
            onClick={() => onOpenReviewQueue(link.id)}
            sx={{ p: 0, minWidth: 0 }}
          >
            {formatMessage(messages.openQueue)}
          </Button>
        ) : (
          formatMessage(messages.inQueue)
        );
      case 'noMatch':
        return formatMessage(messages.noCandidate);
      case 'linked':
        return link.reviewedByName
          ? formatMessage(messages.reviewedBy, { name: link.reviewedByName })
          : null;
      case 'rejected':
        return link.reviewedByName
          ? formatMessage(messages.rejectedBy, { name: link.reviewedByName })
          : null;
      case 'unlinked':
        return formatMessage(messages.unlinkedOn, {
          date: formatDate({ date: link.updatedAt, formatter: 'date' }),
        });
      default:
        return null;
    }
  };

  const headers = [
    messages.patient,
    messages.externalId,
    messages.status,
    messages.match,
    messages.enrolled,
    messages.lastSynced,
    messages.actions,
  ];

  const filters: { value: StatusFilter; count: number }[] = [
    { value: 'all', count: links.length },
    ...LINK_STATUS_ORDER.map((status) => ({
      value: status,
      count: counts.get(status) ?? 0,
    })),
  ];

  return (
    <Paper variant="outlined">
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ sm: 'center' }}
        sx={{ p: 2 }}
      >
        <Typography variant="subtitle1" fontWeight="bold" sx={{ flex: 1 }}>
          {formatMessage(messages.title)}
        </Typography>
        <Button
          variant="contained"
          size="small"
          onClick={() => setEnrollOpen(true)}
        >
          {formatMessage(messages.enroll)}
        </Button>
      </Stack>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        sx={{ px: 2, pb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Stack
          direction="row"
          spacing={0.75}
          flexWrap="wrap"
          useFlexGap
          role="group"
          aria-label={formatMessage(messages.filterLabel)}
          sx={{ flex: 1 }}
        >
          {filters.map(({ value, count }) => (
            <Chip
              key={value}
              size="small"
              clickable
              aria-pressed={filter === value}
              color={filter === value ? 'primary' : 'default'}
              variant={filter === value ? 'filled' : 'outlined'}
              label={`${formatMessage(
                value === 'all' ? messages.all : linkStatusMessages[value]
              )} ${count}`}
              onClick={() => {
                setFilter(value);
                setPage(0);
              }}
            />
          ))}
        </Stack>
        <TextField
          size="small"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(0);
          }}
          placeholder={formatMessage(messages.search)}
          inputProps={{ 'aria-label': formatMessage(messages.search) }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon name="magnifying-glass" color="text.secondary" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: { md: 280 } }}
        />
      </Stack>

      {error ? (
        <Box sx={{ p: 2 }}>
          <StatePanel
            variant="error"
            title={formatMessage(messages.loadFailed)}
            body={error.message}
            onRetry={onRetry}
          />
        </Box>
      ) : (
        <TableContainer>
          <Table size="small" sx={{ minWidth: 960 }}>
            <TableHead>
              <TableRow>
                {headers.map((header) => (
                  <TableCell key={header.id}>
                    <Typography
                      variant="overline"
                      fontWeight="bold"
                      color="text.secondary"
                      lineHeight={1.5}
                    >
                      {formatMessage(header)}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={headers.length}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ py: 2, textAlign: 'center' }}
                    >
                      {formatMessage(
                        loading && links.length === 0
                          ? messages.loadingLinks
                          : links.length === 0
                            ? messages.noLinks
                            : messages.empty
                      )}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
              {pageRows.map((link) => {
                const name = nameOf(link);
                const details = [
                  link.patient?.birthDate
                    ? formatMessage(messages.dob, {
                        date: link.patient.birthDate,
                      })
                    : null,
                  sexAbbreviation(link.patient?.gender),
                ]
                  .filter(Boolean)
                  .join(' · ');
                const note = statusNote(link);
                const canAct = link.status !== 'unlinked';
                return (
                  <TableRow key={link.id}>
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color={link.patient ? 'text.primary' : 'text.secondary'}
                      >
                        {name}
                      </Typography>
                      {details ? (
                        <Typography variant="caption" color="text.secondary">
                          {details}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontFamily: 'monospace' }}
                      >
                        {link.externalPatientId ?? '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack alignItems="flex-start" spacing={0.5}>
                        <PatientLinkStatusChip status={link.status} />
                        {note ? (
                          <Typography variant="caption" color="text.secondary">
                            {note}
                          </Typography>
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {link.matchScore === null
                          ? '-'
                          : link.matchScore.toFixed(2)}
                      </Typography>
                      {link.matchAlgorithm ? (
                        <Typography variant="caption" color="text.secondary">
                          {link.matchAlgorithm}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {link.enrolledByName ? (
                        <Typography variant="body2">
                          {link.enrolledByName}
                        </Typography>
                      ) : null}
                      <Typography variant="caption" color="text.secondary">
                        {formatDate({
                          date: link.createdAt,
                          formatter: 'date',
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {/* No per-patient sync time is recorded yet. */}
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        disabled={!canAct || busyIds.has(link.id)}
                        aria-label={formatMessage(messages.rowMenu, { name })}
                        onClick={(event) =>
                          setMenu({
                            anchor: event.currentTarget,
                            linkId: link.id,
                          })
                        }
                      >
                        <Icon name="ellipsis-vertical" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!error && visible.length > ROWS_PER_PAGE ? (
        <TablePagination
          component="div"
          count={visible.length}
          page={currentPage}
          onPageChange={(_event, next) => setPage(next)}
          rowsPerPage={ROWS_PER_PAGE}
          rowsPerPageOptions={[ROWS_PER_PAGE]}
        />
      ) : null}

      <Menu
        anchorEl={menu?.anchor}
        // Closes on its own if the row's link disappears or ends in a refetch.
        open={Boolean(menuLink && menuLink.status !== 'unlinked')}
        onClose={() => setMenu(null)}
      >
        {menuLink && PAUSABLE_LINK_STATUSES.has(menuLink.status) ? (
          <MenuItem onClick={() => void togglePause(menuLink)}>
            {formatMessage(messages.pause)}
          </MenuItem>
        ) : null}
        {menuLink?.status === 'paused' ? (
          <MenuItem onClick={() => void togglePause(menuLink)}>
            {formatMessage(messages.resume)}
          </MenuItem>
        ) : null}
        {menuLink ? (
          <MenuItem
            onClick={() => {
              setUnlinkTarget(menuLink);
              setUnlinkOpen(true);
              setMenu(null);
            }}
            sx={{ color: 'error.main' }}
          >
            {formatMessage(messages.unlink)}
          </MenuItem>
        ) : null}
      </Menu>

      <UnlinkPatientDialog
        open={unlinkOpen}
        patientName={unlinkTarget ? nameOf(unlinkTarget) : ''}
        vendor={vendor}
        loading={unlinking}
        onCancel={() => setUnlinkOpen(false)}
        onConfirm={() => {
          void confirmUnlink();
        }}
      />

      <EnrollPatientDialog
        open={enrollOpen}
        ehrConnectionId={ehrConnectionId}
        organizationName={organizationName}
        connectionLabel={connectionLabel}
        onClose={() => setEnrollOpen(false)}
        onEnrolled={(enrolledName) =>
          enqueueSnackbar({
            variant: 'success',
            message: formatMessage(messages.enrolledSnack, {
              name: enrolledName || formatMessage(messages.missingPatient),
            }),
          })
        }
      />
    </Paper>
  );
}
